import { type BoardColumn, type BoardView } from '@apm/contracts';

/**
 * Ghép cột Backlog **ảo** vào board, và gỡ những việc nó giữ khỏi cột trạng thái.
 *
 * APM-SPRINT-e.
 *
 * ## ⚠️ Vì sao phải gỡ, chứ không chỉ ghép thêm một cột
 *
 * `GET /board` chỉ lọc theo sprint khi **có** một sprint để lọc. Dự án Agile
 * chưa có sprint nào rơi vào nhánh còn lại, và ở đó nó trả về *mọi* việc của dự
 * án — kể cả việc `sprint_id IS NULL`, tức đúng những việc `/backlog` vừa trả
 * về. Ghép thẳng hai nguồn thì **cùng một task nằm ở hai cột**.
 *
 * Nó không dừng ở chuyện thừa một thẻ. `TaskCard` khai `useDraggable({ id:
 * task.id })` và `useDroppable({ id: task.id })`, mà dnd-kit giữ một registry
 * phẳng theo id: hai node cùng id thì node đăng ký sau đè node trước, nên hình
 * chữ nhật dnd-kit đo được là của *thẻ kia*. Cú kéo vì thế bắt đầu bằng một cú
 * nhảy — thẻ rời khỏi con trỏ và bay ra xa chỗ bấm.
 *
 * Và cú thả thì không sửa được gì: kéo thẻ ở cột Hoàn thành về Backlog gọi
 * `moveToSprint(null)` trên một việc **vốn đã** `sprint_id IS NULL`, nên server
 * không đổi gì, cột Hoàn thành vẫn giữ nó, còn Backlog thì đã có sẵn một bản —
 * người dùng đọc thành "kéo xong nó tự nhân đôi".
 *
 * ## Vì sao lọc ở client, không ở server
 *
 * Cùng một phản hồi `/board` còn nuôi cách nhìn Danh sách và Gantt, và ở đó
 * việc chưa vào sprint **phải** hiện ra — lọc chúng đi ở server là làm trống
 * bảng việc của mọi dự án Agile chưa mở sprint. Cột Backlog là cách riêng của
 * board để nói "chưa vào sprint", nên chuyện gỡ trùng cũng là của riêng board.
 */
export function mergeBacklogColumn(data: BoardView, backlog: BoardColumn | null): BoardView {
  if (!backlog) return data;

  const inBacklog = new Set(backlog.tasks.map((task) => task.id));

  return {
    ...data,
    columns: [
      backlog,
      ...data.columns.map((column) => {
        const tasks = column.tasks.filter((task) => !inBacklog.has(task.id));
        if (tasks.length === column.tasks.length) return column;

        /*
          `count` là số việc server đếm cho trần WIP, không phải `tasks.length`
          — mốc (`kind = milestone`) nằm trong `tasks` nhưng không được đếm.
          Trừ đúng số vừa bỏ đi, thay vì gán lại, để phép đếm của server vẫn là
          phép đếm đang dùng.
        */
        const removed = column.tasks.length - tasks.length;
        return { ...column, tasks, count: Math.max(0, column.count - removed) };
      }),
    ],
  };
}
